import ChatApp from './ChatApp'
import { createRoot, Root } from 'react-dom/client'
import spriteUrl from '/assets/svg/icon-sprite.svg'
import { ChatType } from '../types/webComponent'
import { ChatMembersType } from '../types/store'
import Store from '../redux/store'
import { pickPresence, type PresenceView } from '../utils/presence'

class ChatElement extends HTMLElement {
  private root: Root | null = null
  private mountPoint: HTMLDivElement | null = null
  private modalRoot: HTMLDivElement | null = null
  private dropdownRoot: HTMLDivElement | null = null
  private startChatFn?: (userId: number) => void
  private unsubscribeStore: (() => void) | null = null
  private lastUserStatus: unknown = null
  private presenceFrame: number | null = null

  async connectedCallback() {
    // Presence bridge for the host page. Before the early return below so a
    // re-attached element subscribes again; synchronous so a removal during the
    // awaits further down (disconnectedCallback) unsubscribes it.
    if (!this.unsubscribeStore) {
      this.lastUserStatus = null
      this.unsubscribeStore = Store.subscribe(this.handleStoreChange)
      // Announce this element: the host may already hold statuses from an
      // earlier chat element (e.g. after the /messages swap).
      this.handleStoreChange()
    }

    // ✅ DO NOT recreate shadow root
    if (this.shadowRoot) return

    const shadow = this.attachShadow({ mode: 'open' })

    // Keep wheel scrolling over the widget from reaching the host page. The
    // scrollable lists handle this themselves via `overscroll-behavior: contain`,
    // but wheel events over NON-scrollable areas (message input row, window
    // header, tabs) have no scroller in their chain and would scroll the page.
    // `passive: false` is required for preventDefault on wheel events.
    shadow.addEventListener('wheel', this.containWheel, { passive: false })

    // SVG sprite
    const spriteContainer = document.createElement('div')
    spriteContainer.style.display = 'none'

    fetch(spriteUrl)
      .then((r) => r.text())
      .then((svg) => {
        spriteContainer.innerHTML = svg
      })

    shadow.appendChild(spriteContainer)

    // 🔒 Hide until styles are ready
    this.mountPoint = document.createElement('div')
    this.mountPoint.style.visibility = 'hidden'
    shadow.appendChild(this.mountPoint)

    // Load styles
    // Versioned by the stylesheet's content hash (stamped in at build time by
    // vite.config.ts): its own URL never changes, so without this a browser can
    // pair a new script with a cached old stylesheet.
    const styleUrl = new URL('../meetzy-react.css', import.meta.url)
    styleUrl.searchParams.set('v', '__CHAT_CSS_VERSION__')
    await Promise.all([this.loadStyle(shadow, styleUrl.toString())])

    // Styles ready → show + mount React
    this.mountPoint.style.visibility = 'visible'

    // modal root (BODY ONLY — not shadow)
    this.modalRoot = document.createElement('div')
    this.modalRoot.id = 'modal-root'
    shadow.appendChild(this.modalRoot)

    this.dropdownRoot = document.createElement('div')
    this.dropdownRoot.id = 'dropdown-root'
    shadow.appendChild(this.dropdownRoot)

    // react mount point
    this.mountPoint = document.createElement('div')
    shadow.appendChild(this.mountPoint)

    // ✅ GUARARD: create the React root only once. renderApp() can then be
    // re-run cheaply when a deep-link attribute changes — React reuses the same
    // component instance, so the query cache and all state survive.
    if (!this.root && this.mountPoint) {
      this.root = createRoot(this.mountPoint)
    }

    this.renderApp()
  }

  /**
   * Agent status for the host page (DB-1609), worked out by the same rule the
   * chat uses for its own dots. Null when the user is not known (yet).
   */
  getPresence(hashkey: string): PresenceView | null {
    const { userStatuses, byHashkey, presenceNow } = Store.getState().userStatus
    const userIds = hashkey ? byHashkey[hashkey] : undefined
    if (!userIds?.length) return null
    // Several chat accounts can share an agent's hashkey: show the most active.
    return pickPresence(
      userIds.map((userId) => userStatuses[userId]),
      presenceNow,
    )
  }

  // Tell the host page to re-read getPresence() whenever statuses change or
  // the minute tick moves, at most once per frame.
  private handleStoreChange = () => {
    const userStatus = Store.getState().userStatus
    if (userStatus === this.lastUserStatus) return
    this.lastUserStatus = userStatus
    if (this.presenceFrame !== null) return
    this.presenceFrame = requestAnimationFrame(() => {
      this.presenceFrame = null
      this.dispatchEvent(new CustomEvent<null>('presence-change', { detail: null, bubbles: true, composed: true }))
    })
  }

  /**
   * Only the deep-link attributes are observed: the host rewrites them when the
   * user taps a notification for a different chat while the widget is already
   * mounted (client-side navigation doesn't remount it). `token`/`user` are
   * still read once at mount, as before.
   */
  static get observedAttributes() {
    return ['select-chat-id', 'select-chat-type']
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return
    // Attributes can change before connectedCallback has finished loading styles
    // and created the root; that first render happens there anyway.
    if (this.root) {
      this.renderApp()
    }
  }

  private renderApp() {
    if (!this.root) return

    const rawUser = this.getAttribute('user')
    const token = this.getAttribute('token')
    // Main-platform JWT for the file service (uploads). Optional.
    const serviceToken = this.getAttribute('service-token')
    const type = this.getAttribute('type') ?? ChatType.MESSAGES
    const user = rawUser ? JSON.parse(rawUser) : null
    // Deep link target, e.g. from tapping an FCM push notification.
    const selectChatId = this.getAttribute('select-chat-id')
    const selectChatType = this.getAttribute('select-chat-type') as 'direct' | 'group' | null

    this.root.render(
      <ChatApp
        modalContainer={this.modalRoot}
        dropdownContainer={this.dropdownRoot}
        user={user}
        token={token}
        serviceToken={serviceToken}
        type={type as ChatType}
        selectChatId={selectChatId}
        selectChatType={selectChatType}
        onResetUnread={this.emitResetUnread}
        onRegisterStartChat={this.registerStartChat}
        onLoading={this.emitOnLoading}
        onViewProfile={this.emitViewProfile}
        onOpenStory={this.emitOpenStory}
      />,
    )
  }

  private loadStyle(shadow: ShadowRoot, href: string) {
    return new Promise<void>((resolve) => {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.onload = () => resolve()
      shadow.appendChild(link)
    })
  }

  private emitViewProfile = (agentId: ChatMembersType | null) => {
    this.dispatchEvent(
      new CustomEvent<ChatMembersType>('view-profile', {
        detail: agentId as ChatMembersType, // ✅ pass number here
        bubbles: true,
        composed: true, // ✅ escape shadow DOM
      }),
    )
  }

  private emitOpenStory = (storyHashkey: string) => {
    this.dispatchEvent(
      new CustomEvent<string>('open-story', {
        detail: storyHashkey,
        bubbles: true,
        composed: true, // ✅ escape shadow DOM
      }),
    )
  }

  private emitOnLoading = (status: null) => {
    this.dispatchEvent(
      new CustomEvent<null>('reset-loading', {
        detail: status, // ✅ pass number here
        bubbles: true,
        composed: true, // ✅ escape shadow DOM
      }),
    )
  }

  private emitResetUnread = (count: number) => {
    this.dispatchEvent(
      new CustomEvent<number>('reset-unread', {
        detail: count, // ✅ pass number here
        bubbles: true,
        composed: true, // ✅ escape shadow DOM
      }),
    )
  }

  private registerStartChat = (fn: (userId: number) => void) => {
    this.startChatFn = fn
  }

  /**
   * Cancel wheel events whose ancestor chain inside the widget has no element
   * that can actually scroll — otherwise the browser chains them to the host
   * page. Events that do hit a scrollable list are left alone: the list
   * consumes them and its `overscroll-behavior: contain` stops the chaining
   * at the scroll edges.
   */
  private containWheel = (event: Event) => {
    for (const target of event.composedPath()) {
      if (target === this.shadowRoot) break
      if (!(target instanceof HTMLElement)) continue
      const overflowY = getComputedStyle(target).overflowY
      // The 1px slack keeps sub-pixel rounding (common on textareas) from
      // letting a not-really-scrollable field bypass the guard.
      if ((overflowY === 'auto' || overflowY === 'scroll') && target.scrollHeight - target.clientHeight > 1) {
        return
      }
    }
    event.preventDefault()
  }

  startChat(userId: number) {
    this.startChatFn?.(userId)
  }

  disconnectedCallback() {
    // ✅ NEVER attachShadow here
    this.unsubscribeStore?.()
    this.unsubscribeStore = null
    this.lastUserStatus = null
    if (this.presenceFrame !== null) {
      cancelAnimationFrame(this.presenceFrame)
      this.presenceFrame = null
    }
    this.shadowRoot?.removeEventListener('wheel', this.containWheel)
    this.root?.unmount()
    this.root = null

    if (this.modalRoot) {
      this.modalRoot.remove()
      this.modalRoot = null
    }
    if (this.dropdownRoot) {
      this.dropdownRoot.remove()
      this.dropdownRoot = null
    }

    this.mountPoint = null
  }
}

customElements.define('dubai-brokers-chat', ChatElement)
